import { useAppDispatch, useAppSelector } from 'appState/store'
import { AxiosError } from 'axios'
import { BreadcrumbHeader } from 'components/BreadcrumbSection/BreadcrumbHeader'

import { ErrorBox } from '@outbound-ai/ursa'
import { LoadingBox } from '@outbound-ai/ursa'
import { RefreshIntervalsEnum } from 'configs/apiConfigs'
import { useCall } from 'features/calls/callsHooks'
import { callsSliceLiveCallInitialState, updateLiveCall } from 'features/calls/callsSlice'
import { CallChat, CallInfo, CallNotes, DialPad } from 'features/calls/components'
import { CallStatusEnum, CallTypeToJobTypeEnum } from 'features/calls/types'
import { useClaim, useClaimCalls, useLevelofSupport } from 'features/claims/claimsHooks'
import { getCallIndexFromClaim, isCurrentUserClaimOwner } from 'features/claims/utils/claimsParsers'
import { getClaimCallSummaryPath, getClaimDetailPath, getClaimsPath } from 'features/claims/utils/claimsPathUtils'
import { useEntities } from 'features/entities/entityHooks'
import { createElement, useEffect } from 'react'
import { Breadcrumb } from 'react-bootstrap'
import { renderToStaticMarkup } from 'react-dom/server'
import { FaCaretRight } from 'react-icons/fa'
import { IoArrowBack } from 'react-icons/io5'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { errorHandler } from 'utils/errors/errors'
import { notificator } from 'utils/notificator/notificator'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'

type RouteParamsType = {
  callId: string
  oaiClaimId: string
  tenantId: string
}

const ClaimCalls = () => {
  // global state
  const { showDialPad } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)
  const { user } = useAppSelector((state) => state.auth)

  // utils
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { callId, oaiClaimId, tenantId: tenantIdFromParams } = useParams<RouteParamsType>()
  
  // Get tenant from URL params or fallback to helper (for old URL compatibility)
  const tenantId = tenantIdFromParams || getTntIdForNavigation()
  // api
  const { data: call, error: callError } = useCall(callId, {
    refreshInterval: RefreshIntervalsEnum.useCallForTalkToPayorRepCalls,
  })
  const { data: claim, error: claimError, isLoading: isLoadingClaim } = useClaim(oaiClaimId!)
  const { data: calls, isLoading: isLoadingCalls } = useClaimCalls(oaiClaimId!, {
    onError: (err: AxiosError) =>
      errorHandler.handleError(err, `${ErrorMessagesEnum.CallsError} The call count might not available.`),
  })
  localStorage.setItem('levelOfSupport', JSON.stringify(useLevelofSupport().data))
  const { isLoading: isLoadingEntities } = useEntities()

  // effects
  useEffect(() => {
    if (call) {
      if (
        [CallStatusEnum.Complete, CallStatusEnum.FailedToQueue, CallStatusEnum.FailedToComplete].includes(call?.status)
      ) {
        if (call?.status === CallStatusEnum.FailedToQueue) {
          notificator.notify({
            heading: 'Call failed to queue, please report this to the administrator',
            type: 'error',
          })
          //notificator.error('Call failed to queue, please report this to the administrator')
        }

        if (call?.status === CallStatusEnum.FailedToComplete) {
          notificator.notify({
            heading: 'Call failed to complete, please report this to the administrator',
            type: 'error',
          })
          //notificator.error('Call failed to complete, please report this to the administrator')
        }
        dispatch(updateLiveCall(callsSliceLiveCallInitialState))
        if (call?.jobType === CallTypeToJobTypeEnum.diagnosticHeadset) {
          return
        }

        redirectToClaimCallSummary()
      }
    }
  }, [call])

  const redirectToClaimCallSummary = () => {
    navigate(getClaimCallSummaryPath(tenantId!, oaiClaimId!, callId!))
  }

  // handlers
  // const handleMinimizeCall = useCallback(() => {
  //   if (location.key) return navigate(-1)
  //   if (claim?.oaiClaimId) return navigate(paths.claim(claim.oaiClaimId))
  //   return navigate(PathsEnum.claims)
  // }, [])

  // variables
  const breadCrumbTitle = calls ? `#${getCallIndexFromClaim(calls, callId!)}` : ''
  const error = claimError || callError
  const isLoading = (isLoadingCalls || isLoadingClaim || isLoadingEntities) && !error
  const isOwner = isCurrentUserClaimOwner(claim?.ownerId, user!)

  const redirectToClaimDetails = () => {
    navigate(getClaimDetailPath(tenantId!, oaiClaimId!))
  }

  const redirectToClaimDetailsPath = () => {
    return getClaimDetailPath(tenantId!, oaiClaimId!)
  }

  const redirectToClaims = () => {
    navigate(getClaimsPath(tenantId!))
  }

  return (
    <Div>
      {showDialPad && <DialPad />}

      {error && <ErrorBox {...errorHandler.errorToErrorBoxProps(error)} />}

      {isLoading && <LoadingBox message="Loading call..." />}

      {claim && call && (
        <>
          <BreadcrumbHeader
            title={`Call ${breadCrumbTitle}`}
            navComponent={
              <Breadcrumb>
                <Breadcrumb.Item onClick={() => redirectToClaims()}>Claims</Breadcrumb.Item>
                <Breadcrumb.Item onClick={() => redirectToClaimDetails()}>Claim Details</Breadcrumb.Item>
                <Breadcrumb.Item active>Live Call</Breadcrumb.Item>
              </Breadcrumb>
            }
            backAction={() => redirectToClaimDetails()}
            breadcrumbs={[
              { title: 'Claims', path: redirectToClaimDetailsPath() },
              { title: 'Claim Details', path: redirectToClaimDetailsPath() },
              { title: 'Call Closeup' },
            ]}
            showBackButton={true}
          >
            {/* <Button variant="outlined" onClick={handleMinimizeCall}>
              <FaMinus /> Minimze Call Window
            </Button> */}
            <Button variant="primary-outline" radius="rounded" onClick={() => redirectToClaimDetails()}>
              <IoArrowBack /> Back to Claim Details Page
            </Button>
          </BreadcrumbHeader>
          <section className="live-call-container">
            <aside className="call-info">
              <CallInfo claim={claim!} />
            </aside>
            <section className="main">
              <CallChat call={call} claim={claim} />
            </section>
            <aside>
              <CallNotes disabled={!isOwner} callId={call.jobId} />
            </aside>
          </section>
        </>
      )}
    </Div>
  )
}

const reactSvgComponentToMarkupString = (Component: any, props: any) =>
  `data:image/svg+xml,${encodeURIComponent(renderToStaticMarkup(createElement(Component, props)))}`

const Div = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - ${({ theme }) => theme.constants.navbarHeight});
  position: relative;

  .dropdown {
    margin-right: 0.5rem;
  }

  .breadcrumb {
    position: relative;
    top: 15px;
    background-color: none;
    li {
      a {
        ${({ theme }) => theme.typography.xs.medium};
        color: ${({ theme }) => theme.colors.info.neutral};
        cursor: pointer;
        text-decoration: none !important;
      }
    }
    .breadcrumb-item.active {
      ${({ theme }) => theme.typography.xs.medium};
      color: ${({ theme }) => theme.colors.grey.dark};
    }

    .breadcrumb-item + .breadcrumb-item::before {
      content: ${({ theme }) =>
        `url(${reactSvgComponentToMarkupString(FaCaretRight, {
          color: theme.colors.grey.neutral,
        })})`}!important;
      margin-top: 2px;
    }
  }

  .live-call-container {
    width: 100%;
    height: ${({ theme }) => theme.util.subtractFromAbsoluteViewportHeight(theme.constants.navbarHeight, 2)};
    display: grid;
    grid-template-columns: 25% 50% 25%;
    .call-info {
      background: #f9fafb;
    }
  }

  aside:first-child {
    ::-webkit-scrollbar {
      display: none;
    }
    overflow-x: scroll;
  }

  aside .loading-container {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  aside,
  .main {
    height: ${({ theme }) => theme.util.subtractFromAbsoluteViewportHeight(theme.constants.navbarHeight, 2)};
  }
  .main {
    border-left: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    border-right: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
`

export { ClaimCalls }
